import React from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import CircularProgress from "@mui/material/CircularProgress";
import BaseDataChart, { BaseDataChartProps } from './BaseDataChart';

interface TreemapNode {
    name: string;
    size: number;
}

interface Props extends BaseDataChartProps {
    colors?: string[];
    footer?: React.ReactNode;
}

const DEFAULT_COLORS = [
    '#FFC400',
    '#FF3F7F',
    '#8C00FF',
    '#450693',
    '#008BFF',
    '#FF2DF1',
    '#00FF9C',
];

const TreemapDataChart: React.FC<Props> = ({
                                               title,
                                               data,
                                               className,
                                               height = 380,
                                               width = 520,
                                               colors = DEFAULT_COLORS,
                                               loading = false,
                                               footer,
                                           }) => {

    const flatData = Object.entries(data.data).map(([name, values]) => ({
        name: name.replace(/_/g, " "),
        size: Object.values(values)[0] || 0, // Take first category value
    }));

    // Custom content renderer for treemap cells
    const CustomizedContent = (props: any) => {
        const { x, y, width, height, index, name, size } = props;

        return (
            <g>
                <rect
                    x={x}
                    y={y}
                    width={width}
                    height={height}
                    style={{
                        fill: colors[index % colors.length],
                        stroke: '#fff',
                        strokeWidth: 2,
                    }}
                />
                {width > 50 && height > 30 && (
                    <>
                        <text
                            className="p-1 text-sm/6 font-normal"
                            x={x + width / 2}
                            y={y + height / 2 - 7}
                            textAnchor="middle"
                            fill="#fff"
                        >
                            {name}
                        </text>
                        <text
                            className="p-1 text-sm/6 font-normal"
                            x={x + width / 2}
                            y={y + height / 2 + 10}
                            textAnchor="middle"
                            fill="#fff"
                        >
                            {size}
                        </text>
                    </>
                )}
            </g>
        );
    };

    const renderChart = () => {
        if (loading) {
            return <CircularProgress />;
        }

        if (!data || flatData.length === 0) {
            return <div className="text-gray-400">Keine Daten vorhanden</div>;
        }

        return (
            <ResponsiveContainer width="100%" height="100%" className="text-sm/6 font-medium">
                <Treemap
                    data={flatData}
                    dataKey="size"
                    stroke="#fff"
                    fill="#8884d8"
                    animationDuration={400}
                    content={<CustomizedContent />}
                >
                    <Tooltip
                        labelClassName="p-1 text-sm/6 font-medium"
                        content={({ payload }) => {
                            if (payload && payload.length > 0) {
                                const data = payload[0].payload;
                                return (
                                    <div
                                        style={{
                                            backgroundColor: 'rgba(255, 255, 255, 0.95)',
                                            padding: '8px 12px',
                                            border: '1px solid #ccc',
                                            borderRadius: '4px',
                                        }}
                                    >
                                        <p style={{ margin: 0, fontWeight: 'bold' }}>{data.name}</p>
                                        <p style={{ margin: '4px 0 0 0' }}>Anzahl: {data.size}</p>
                                    </div>
                                );
                            }
                            return null;
                        }}
                    />
                </Treemap>
            </ResponsiveContainer>
        );
    };

    return (
        <BaseDataChart
            title={title}
            data={data}
            className={className}
            height={height}
            width={width}
            loading={loading}
            footer={footer}
        >
            {renderChart()}
        </BaseDataChart>
    );
};

export default TreemapDataChart;